/**
 * Page View Tracker Plugin
 * Automatically tracks page views with page type and product information
 */

import { watch } from 'vue'
import { useCookieConsent } from '~/composables/useCookieConsent'
import { resolveProductViewItemTracking } from '~/utils/ecommerceProductTracking'

export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined') {
    return
  }

  const route = useRoute()
  const { getConsent } = useCookieConsent()
  type TrackPageView = (
    pageType: string,
    productType?: string,
    productVariant?: string,
    language?: string
  ) => Promise<void>
  type TrackViewItem = (
    productId: string,
    productName: string,
    price: number,
    category?: string,
    color?: string,
    size?: string
  ) => Promise<void>
  type TrackingApi = {
    trackPageView: TrackPageView
    trackViewItem: TrackViewItem
  }
  let trackingImportPromise: Promise<TrackingApi> | null = null
  let lastTrackedKey: string | null = null
  let pendingTrackKey: string | null = null

  const getTrackingApi = async () => {
    if (!trackingImportPromise) {
      trackingImportPromise = import('~/composables/useGA').then(({ useGA }) => {
        const ga = useGA()
        return {
          trackPageView: ga.trackPageView,
          trackViewItem: ga.trackViewItem
        }
      })
    }

    return trackingImportPromise
  }

  const hasAnalyticsConsent = () => getConsent().analytics === true
  const getTrackKey = () => route.fullPath || route.path

  // Helper function to determine page type from route
  const getPageType = (path: string): string => {
    // Remove language prefix but keep leading slash for matching
    const cleanPath = path.replace(/^\/fr/, '') || '/'
    
    // Product pages (e.g., /pumps/red, /ankle-boots/black)
    if (cleanPath.match(/^\/(pumps|boots|ankle-boots|sandals|gloves)\/[^/]+$/)) {
      return 'product'
    }
    
    // Category pages (e.g., /pumps, /boots)
    if (cleanPath.match(/^\/(pumps|boots|ankle-boots|sandals|gloves)$/)) {
      return 'category'
    }
    
    // Specific pages
    if (cleanPath === '/' || cleanPath === '') return 'home'
    if (cleanPath.startsWith('/magazine')) return 'magazine'
    if (cleanPath.startsWith('/people')) return 'people'
    if (cleanPath.startsWith('/press')) return 'press'
    if (cleanPath.startsWith('/brand')) return 'brand'
    if (cleanPath.startsWith('/fabrication')) return 'fabrication'
    if (cleanPath.startsWith('/comfort')) return 'comfort'
    if (cleanPath.startsWith('/store')) return 'store'
    if (cleanPath.startsWith('/contact')) return 'contact'
    if (cleanPath.startsWith('/checkout')) return 'checkout'
    if (cleanPath.startsWith('/account')) return 'account'
    if (cleanPath.startsWith('/cart')) return 'cart'
    
    return 'other'
  }

  // Helper function to extract product info from route
  const getProductInfo = (path: string): { productType?: string, productVariant?: string } => {
    // Match product pages: /pumps/red, /ankle-boots/black, etc.
    const productMatch = path.match(/\/(pumps|boots|ankle-boots|sandals|gloves)\/([^/]+)/)
    if (productMatch) {
      return {
        productType: productMatch[1],
        productVariant: productMatch[2] // color
      }
    }
    return {}
  }

  // Get current language
  const getCurrentLanguage = (): string => {
    return route.path.startsWith('/fr') ? 'fr' : 'en'
  }

  // Track page view on route change
  const trackCurrentPage = async () => {
    const trackKey = getTrackKey()

    if (!hasAnalyticsConsent()) {
      pendingTrackKey = trackKey
      return
    }

    if (lastTrackedKey === trackKey) {
      pendingTrackKey = null
      return
    }

    const path = route.path
    const pageType = getPageType(path)
    const productInfo = getProductInfo(path)
    const language = getCurrentLanguage()
    const tracking = await getTrackingApi()

    lastTrackedKey = trackKey
    pendingTrackKey = null

    tracking.trackPageView(
      pageType,
      productInfo.productType,
      productInfo.productVariant,
      language
    ).catch(err => {
      console.warn('Error tracking page view:', err)
      if (lastTrackedKey === trackKey) {
        lastTrackedKey = null
      }
      pendingTrackKey = trackKey
    })

    const productViewItem = resolveProductViewItemTracking(path)
    if (productViewItem) {
      tracking.trackViewItem(
        productViewItem.itemId,
        productViewItem.itemName,
        productViewItem.price,
        productViewItem.itemCategory,
        productViewItem.color
      ).catch(err => {
        console.warn('Error tracking product view_item:', err)
      })
    }
  }

  void trackCurrentPage()

  // Track on route changes
  watch(() => route.fullPath, () => {
    void trackCurrentPage()
  })

  // If consent is granted after landing, replay the current page view once.
  watch(() => getConsent().analytics, (granted) => {
    if (!granted) {
      return
    }

    const currentTrackKey = getTrackKey()
    if (pendingTrackKey === currentTrackKey || lastTrackedKey !== currentTrackKey) {
      void trackCurrentPage()
    }
  })
})
